import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Upload, CheckCircle2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Uavhengig av api.js (som ville lagt til auth-header) — dette er offentlig.
const publicApi = axios.create({ baseURL: API, withCredentials: false });

function fieldVisible(field, data) {
  const cond = field.showIf || {};
  for (const [k, v] of Object.entries(cond)) {
    if (String(data[k] ?? "") !== String(v)) return false;
  }
  return true;
}

function Section({ section, data, setData, mediaByField, setMediaByField, uploading, setUploading, formSlug }) {
  const visibleFields = section.fields.filter((f) => fieldVisible(f, data));
  if (visibleFields.length === 0) return null;

  const setVal = (k, v) => setData((d) => ({ ...d, [k]: v }));

  return (
    <div className="border border-white/10 p-6 space-y-5">
      <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">{section.title}</h2>
      <div className="space-y-4">
        {visibleFields.map((f) => (
          <Field
            key={f.key}
            field={f}
            value={data[f.key]}
            onChange={(v) => setVal(f.key, v)}
            media={mediaByField[f.key] || []}
            setMedia={(m) => setMediaByField((s) => ({ ...s, [f.key]: m }))}
            uploading={uploading}
            setUploading={setUploading}
            formSlug={formSlug}
          />
        ))}
      </div>
    </div>
  );
}

function Field({ field, value, onChange, media, setMedia, uploading, setUploading, formSlug }) {
  const label = (
    <label className={`block font-manrope text-sm text-[var(--cream)] font-medium mb-2 ${field.indent ? "pl-6" : ""}`}>
      {field.label}
      {field.required && <span className="text-[var(--magenta)]"> *</span>}
    </label>
  );

  if (field.type === "text") {
    return (
      <div className={field.indent ? "pl-6" : ""}>
        {label}
        <input
          type="text"
          value={value || ""}
          maxLength={field.maxLength || 200}
          onChange={(e) => onChange(e.target.value)}
          className="input-editorial w-full"
          data-testid={`field-${field.key}`}
        />
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div className={field.indent ? "pl-6" : ""}>
        {label}
        <textarea
          value={value || ""}
          maxLength={field.maxLength || 1000}
          rows={3}
          onChange={(e) => onChange(e.target.value)}
          className="input-editorial w-full resize-y"
          data-testid={`field-${field.key}`}
        />
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div className={field.indent ? "pl-6" : ""}>
        {label}
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="input-editorial w-full"
          data-testid={`field-${field.key}`}
        >
          <option value="">— Velg —</option>
          {(field.options || []).map((opt) => (
            <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === "yesno") {
    return (
      <div className={field.indent ? "pl-6" : ""}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="font-manrope text-sm text-[var(--cream)] font-medium">
            {field.label}
            {field.required && <span className="text-[var(--magenta)]"> *</span>}
          </div>
          <div className="flex gap-2" data-testid={`field-${field.key}`}>
            <button
              type="button"
              onClick={() => onChange("yes")}
              className={`px-4 py-1.5 text-xs font-meta border transition-colors ${
                value === "yes"
                  ? "bg-[var(--magenta)] border-[var(--magenta)] text-white"
                  : "border-white/20 text-[var(--cream-soft)] hover:border-white/40"
              }`}
              data-testid={`${field.key}-yes`}
            >
              Ja
            </button>
            <button
              type="button"
              onClick={() => onChange("no")}
              className={`px-4 py-1.5 text-xs font-meta border transition-colors ${
                value === "no"
                  ? "bg-white/15 border-white/40 text-[var(--cream)]"
                  : "border-white/20 text-[var(--cream-soft)] hover:border-white/40"
              }`}
              data-testid={`${field.key}-no`}
            >
              Nei
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-[var(--magenta)]"
          data-testid={`field-${field.key}`}
        />
        <span className="font-manrope text-sm text-[var(--cream)]">
          {field.label}
          {field.required && <span className="text-[var(--magenta)]"> *</span>}
        </span>
      </label>
    );
  }

  if (field.type === "age_range") {
    const min = field.min ?? 18;
    const max = field.max ?? 99;
    const v = value && typeof value === "object" ? value : {};
    const setPart = (key, num) => {
      let n = parseInt(num, 10);
      if (Number.isNaN(n)) n = "";
      onChange({ ...v, [key]: n });
    };
    const from = v.from ?? field.default_from ?? min;
    const to = v.to ?? field.default_to ?? max;
    return (
      <div>
        <label className="font-manrope text-sm text-[var(--cream)] block mb-2">
          {field.label}
          {field.required && <span className="text-[var(--magenta)]"> *</span>}
        </label>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[10px] text-[var(--cream-faint)] mb-1">Fra</div>
            <input
              type="number"
              inputMode="numeric"
              min={min}
              max={max}
              value={from}
              onChange={(e) => setPart("from", e.target.value)}
              className="input-editorial w-full"
              data-testid={`field-${field.key}-from`}
            />
          </div>
          <span className="text-[var(--cream-faint)] pt-5">–</span>
          <div className="flex-1">
            <div className="text-[10px] text-[var(--cream-faint)] mb-1">Til</div>
            <input
              type="number"
              inputMode="numeric"
              min={min}
              max={max}
              value={to}
              onChange={(e) => setPart("to", e.target.value)}
              className="input-editorial w-full"
              data-testid={`field-${field.key}-to`}
            />
          </div>
        </div>
        {(from < min || to > max || to < from) && (
          <div className="text-[11px] text-red-400 mt-1">
            Fra må være minst {min}, til maks {max}, og til ≥ fra.
          </div>
        )}
      </div>
    );
  }

  if (field.type === "file_multi") {
    const maxFiles = field.maxFiles || 10;
    const maxSizeMB = field.maxSizeMB || 15;

    const addFiles = async (e) => {
      const files = Array.from(e.target.files || []);
      e.target.value = "";
      if (media.length + files.length > maxFiles) {
        alert(`Maks ${maxFiles} bilder.`);
        return;
      }
      setUploading(true);
      const newMedia = [...media];
      for (const file of files) {
        if (file.size > maxSizeMB * 1024 * 1024) {
          alert(`${file.name}: overskrider ${maxSizeMB} MB`);
          continue;
        }
        try {
          const init = await publicApi.post(`/screening/${formSlug}/init-media`, {
            filename: file.name,
            content_type: file.type,
            size: file.size,
          });
          const { upload_url, path } = init.data;
          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", upload_url, true);
            xhr.setRequestHeader("Content-Type", file.type);
            xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("R2 PUT feilet")));
            xhr.onerror = () => reject(new Error("Nettverksfeil"));
            xhr.send(file);
          });
          newMedia.push({ path, content_type: file.type, size: file.size, name: file.name });
          setMedia([...newMedia]);
        } catch (err) {
          alert(`Feil ved opplasting av ${file.name}: ${err.message || "ukjent"}`);
        }
      }
      setUploading(false);
    };

    const removeAt = (i) => {
      const arr = media.filter((_, idx) => idx !== i);
      setMedia(arr);
    };

    return (
      <div>
        {label}
        <div className="flex flex-wrap gap-3 items-start">
          {media.map((m, i) => (
            <div key={i} className="relative border border-white/10 px-3 py-2 flex items-center gap-2" data-testid={`upload-${i}`}>
              <span className="font-meta text-xs text-[var(--cream-soft)]">{m.name || `bilde ${i + 1}`}</span>
              <button type="button" onClick={() => removeAt(i)} className="text-[var(--magenta)]" data-testid={`remove-${i}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {media.length < maxFiles && (
            <label className={`btn-ghost inline-flex items-center gap-2 cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
              <Upload className="h-3.5 w-3.5" />
              {uploading ? "Laster opp…" : "Legg til bilde"}
              <input type="file" accept={field.accept || "image/*"} multiple className="hidden" onChange={addFiles} data-testid={`field-${field.key}`} />
            </label>
          )}
        </div>
        <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-2">
          Maks {maxFiles} bilder, {maxSizeMB} MB per bilde.
        </div>
      </div>
    );
  }

  return null;
}

export default function Skjema() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [schema, setSchema] = useState(null);
  const [data, setData] = useState({});
  const [mediaByField, setMediaByField] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    // Ingen indeksering
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { try { document.head.removeChild(m); } catch { /* ignore */ } };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await publicApi.get(`/screening/${slug}`);
        if (!alive) return;
        setForm(r.data);
        setSchema(r.data.schema);
      } catch (e) {
        setError(e?.response?.data?.detail || "Skjemaet finnes ikke eller er ikke aktivt.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const allMedia = useMemo(() => {
    return Object.values(mediaByField).flat();
  }, [mediaByField]);

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const r = await publicApi.post(`/screening/${slug}/submit`, {
        data,
        media: allMedia.map((m) => ({ path: m.path, content_type: m.content_type, size: m.size })),
      });
      setSubmitted(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Kunne ikke sende inn skjemaet. Prøv igjen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--noir)] flex items-center justify-center">
        <div className="font-meta text-[var(--cream-soft)]">Laster…</div>
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="min-h-screen bg-[var(--noir)] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Ikke tilgjengelig</h1>
          <p className="font-manrope text-sm text-[var(--cream-soft)]">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[var(--noir)] flex items-center justify-center px-6">
        <div className="max-w-lg text-center border border-white/10 p-10">
          <CheckCircle2 className="h-12 w-12 text-[var(--magenta)] mx-auto mb-4" />
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Takk!</h1>
          <p className="font-manrope text-sm text-[var(--cream-soft)]">
            Innsendingen er mottatt (#{submitted.submission_number}). Du hører fra meg hvis det er aktuelt.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--noir)] py-10 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="text-center mb-8">
          <h1 className="font-fraunces italic text-4xl sm:text-5xl text-[var(--cream)] mb-3" data-testid="form-title">{form.title}</h1>
          {form.description && (
            <p className="font-manrope text-sm text-[var(--cream-soft)] max-w-xl mx-auto">{form.description}</p>
          )}
        </header>

        {schema?.sections.map((s, i) => (
          <Section
            key={i}
            section={s}
            data={data}
            setData={setData}
            mediaByField={mediaByField}
            setMediaByField={setMediaByField}
            uploading={uploading}
            setUploading={setUploading}
            formSlug={slug}
          />
        ))}

        {error && (
          <div className="border-l-2 border-[var(--magenta)] pl-3 py-2 text-sm text-[var(--cream)] font-medium">
            {error}
          </div>
        )}

        <div className="flex justify-center pt-4">
          <button
            onClick={submit}
            disabled={submitting || uploading}
            className="btn-magenta text-base px-8"
            data-testid="submit-btn"
          >
            {submitting ? "Sender…" : "Send inn"}
          </button>
        </div>
      </div>
    </div>
  );
}
